import { ELEMENT_NODE } from '@qdabra/forms-shared/nodeTypes';
import {
    encodeBase64Attachment,
    stringToUtf8Bytes,
} from '@qdabra/infopath-attachments';
import { parseXmlNative, xmlToStringMulti } from '../../../utility/xmlHelpers';
import { getChildNodes, xmlToString } from '../../../xmlUtility';
import {
    paramDestDs,
    paramDestPath,
    paramFormat,
    paramSourceDs,
    paramSourceFile,
    paramSourcePath,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeOption,
    typeXPath,
} from '../constants/parameterTypes';
import { failure } from '../helpers/commandResult';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import {
    selectNodeFromXPathEngine,
    selectNodeListFromXPathEngine,
} from '../helpers/qRules.utility';

const parseXML = parseXmlNative;

const transformXsltFile = 'xsltfile';
const transformToString = 'tostring';
const transformExcludeRoot = 'excluderoot';
const transformResultFilename = 'resultfilename';
const transformPlacement = 'placement';
const transformUseDocument = 'usedocument';
const transformSetResult = 'setresult';

const PLACEMENT_APPEND = 'append';
const PLACEMENT_PREPEND = 'prepend';
const PLACEMENT_REPLACE = 'replace';

const FORMAT_XML = 'xml';
const FORMAT_STRING = 'string';
const FORMAT_BASE64 = 'base64';

const errorFailedToSelectSource = 'Failed to select the source node.';
const errorFailedToSelectDestination = 'Failed to select the destination node.';
const errorSourceConflict = `/${paramSourceDs} and /${paramSourceFile} cannot both be specified.`;
const errorToStringOnRoot =
    "The tostring argument cannot be 'true' when the destination node is a root node.";
const errorNotToStringNonElement =
    "The tostring argument must be 'true' when the destination node is a non-element.";
const errorSetResult = 'An error occurred setting the result of the transform.';
const errorFailedFileLoad = 'Error: Failed to load the specified file.';

const optionalParameters = [
    {
        name: paramSourceDs,
        description: 'Source data source',
        type: typeDataSource,
    },
    {
        name: paramSourcePath,
        description: 'Source node path',
        type: typeXPath,
        dsParentName: paramSourceDs,
    },
    {
        name: paramSourceFile,
        description: 'Source template file',
    },
    {
        name: paramDestDs,
        description: 'Destination data source',
        type: typeDataSource,
    },
    {
        name: paramDestPath,
        description: 'Destination node path',
        type: typeXPath,
        dsParentName: paramDestDs,
    },
    {
        name: transformToString,
        description: '(boolean, false by default) Store result as string value',
        type: typeBoolean,
    },
    {
        name: transformExcludeRoot,
        description:
            'Omit root of transform result when transferring to destination',
        type: typeBoolean,
    },
    {
        name: transformResultFilename,
        description:
            'The filename to use for the resulting file when using the base64 format',
    },
    {
        name: transformPlacement,
        description:
            'Location to place the transform result in the destination node. Possible values are: replace, prepend, append (default is replace)',
        type: typeOption,
        options: [
            {
                name: 'Append',
                value: PLACEMENT_APPEND,
            },
            {
                name: 'Prepend',
                value: PLACEMENT_PREPEND,
            },
            {
                name: 'Replace',
                value: PLACEMENT_REPLACE,
            },
        ],
    },
    {
        name: transformUseDocument,
        description:
            '(boolean, false by default) Allow using the document() function in the XSLT.',
        type: typeBoolean,
    },
    {
        name: transformSetResult,
        description:
            '(boolean, false by default) Set the transform result to the qRules Result field.',
        type: typeBoolean,
    },
    {
        name: paramFormat,
        description: 'Method for storing the result (xml, string, base64)',
        type: typeOption,
        options: [
            {
                name: 'Xml',
                value: FORMAT_XML,
            },
            {
                name: 'String',
                value: FORMAT_STRING,
            },
            {
                name: 'Base 64',
                value: FORMAT_BASE64,
            },
        ],
    },
];

const requiredParameters = [
    {
        name: transformXsltFile,
        description: 'Resource file name of XSL transform',
    },
];

const description =
    'Performs an XSL transform on data in any data source and stores the result somewhere else in the form. Supports using xsl:include and xsl:import to import other XSLTs that are stored in the form as a resource file. The resulting XML can be stored as either a string value in a single field, or as an entire node tree beneath a certain node. This command utilizes an XSLT stored as a resource file in the XSN to perform its transform. You can use the Resource Files dialog to add and manage these files. This command is recommended for experienced users who know how to create an XSLT.';

function getPlacement(placement) {
    const placementLower = placement.toLowerCase();

    switch (placementLower) {
        case PLACEMENT_APPEND:
        case PLACEMENT_PREPEND:
        case PLACEMENT_REPLACE:
            return placementLower;

        default:
            throw new Error(
                `Invalid placement value: ${placement}. Allowed values are: ${PLACEMENT_APPEND}, ${PLACEMENT_PREPEND}, ${PLACEMENT_REPLACE}`,
            );
    }
}

function determineToString(format, toString) {
    const isStringFormat =
        format && (format === FORMAT_STRING || format === FORMAT_BASE64);

    return isStringFormat || toString;
}

function determineFormat(format, toString) {
    if (format) {
        return format;
    }

    return toString ? FORMAT_STRING : FORMAT_XML;
}

function validateTransformSettings(parameters) {
    const destinationNodeType = parameters.destNode.getNode().nodeType;
    // BUGBUG - Is 0 the right value here?
    const destinationIsRoot = destinationNodeType === 0;

    if (parameters.produceStringResult && destinationIsRoot) {
        // The root node (not to be confused with the Document Element)
        // cannot have its value set to a string
        throw new Error(errorToStringOnRoot);
    }

    const isElement = destinationNodeType === ELEMENT_NODE || destinationIsRoot;

    if (!(parameters.produceStringResult || isElement)) {
        // Only elements or the root node can have nodes inserted beneath them
        throw new Error(errorNotToStringNonElement);
    }

    if (parameters.format === FORMAT_BASE64 && !parameters.resultFileName) {
        throw new Error(
            `${transformResultFilename} cannot be blank if format is base64`,
        );
    }
}

function getResultNodes(xPathEngine, doc, excludeRoot) {
    try {
        const path = `${excludeRoot ? '/*' : ''}/node()`;

        return selectNodeListFromXPathEngine(xPathEngine, doc, path);
    } catch (e) {
        console.error(e);
        throw new Error(
            'An error occurred trying to process the result as XML.',
        );
    }
}

function appendNodes(destNode, newNodes) {
    return destNode.appendChildrenAsync(newNodes);
}

async function prependNodeContent(destNode, newNodes) {
    const firstChild = destNode.selectSingle('node()');

    if (!firstChild) {
        await appendNodes(destNode, newNodes);
    }

    for (const node of newNodes) {
        await firstChild.insertBeforeAsync(node);
    }
}

function replaceNodeContent(destNode, newNodes) {
    return destNode.setContentAsync(newNodes);
}

function addNodeContent(destNode, placement, newNodes) {
    if (!destNode) {
        throw new Error('Could not locate the destination node');
    }

    switch (placement) {
        case PLACEMENT_APPEND:
            return appendNodes(destNode, newNodes);

        case PLACEMENT_PREPEND:
            return prependNodeContent(destNode, newNodes);

        default:
            return replaceNodeContent(destNode, newNodes);
    }
}

function getNewValue(oldValue, placement, newValue) {
    switch (placement) {
        case PLACEMENT_PREPEND:
            return newValue + oldValue;

        case PLACEMENT_APPEND:
            return oldValue + newValue;

        default:
            return newValue;
    }
}

function setTransformResultAsync(xPathEngine, parameters, value) {
    var destNode = parameters.destNode;

    switch (parameters.format) {
        case FORMAT_XML:
            return addNodeContent(
                destNode,
                parameters.placement,
                getResultNodes(xPathEngine, value, parameters.excludeRoot),
            );

        case FORMAT_STRING:
            return destNode.setValueAsync(
                getNewValue(destNode.value(), parameters.placement, value),
            );

        case FORMAT_BASE64:
            return destNode.setValueAsync(
                encodeBase64Attachment(
                    stringToUtf8Bytes(value),
                    parameters.resultFileName,
                ),
            );

        default:
            throw new Error(`Invalid format value: ${parameters.format}`);
    }
}

function trySetResult(xPathEngine, parameters, result) {
    try {
        return setTransformResultAsync(xPathEngine, parameters, result);
    } catch (e) {
        console.error('Error setting transform result');
        throw new Error(errorSetResult);
    }
}

// (Template, String) -> Promise[Document]
async function loadXmlTemplateFile(template, sourceFile) {
    try {
        const stringXml = await template.getTemplateFileAsync(sourceFile);

        return parseXML(stringXml);
    } catch (e) {
        throw new Error(`${errorFailedFileLoad} Error: ${e.message}`);
    }
}

// (String, String) -> Promise[Document]
async function getTransformSourceDs(
    dataSources,
    template,
    sourceDsName,
    sourceFile,
) {
    if (!sourceFile) {
        return getDataSource(dataSources, sourceDsName).getNode();
    }

    // Can't specify both sourcefile and sourceds
    if (sourceDsName) {
        throw new Error(errorSourceConflict);
    }

    return await loadXmlTemplateFile(template, sourceFile);
}

async function performTransform(transforms, parameters) {
    const format = parameters.format;
    const toString = parameters.produceStringResult;

    // /format takes precedence over /tostring
    parameters.produceStringResult = determineToString(format, toString);
    parameters.format = determineFormat(format, toString);

    validateTransformSettings(parameters);

    const resultNode = await transforms.transform(
        parameters.xsltFileName,
        parameters.sourceNode,
    );

    return parameters.produceStringResult
        ? xmlToString(resultNode)
        : resultNode;
}

function transformResultToString(transformResult, excludeRoot) {
    if (typeof transformResult === 'string') {
        return transformResult;
    }

    if (!excludeRoot) {
        return xmlToString(transformResult);
    }

    return xmlToStringMulti(getChildNodes(transformResult));
}

async function executeAsync({ dataSources, parameters, template, transforms }) {
    const xsltFileName = getParamValue(parameters, transformXsltFile);
    const sourceDsName = getParamValue(parameters, paramSourceDs);
    const sourcePath = getParamValue(parameters, paramSourcePath, '/');
    const sourceFile = getParamValue(parameters, paramSourceFile);
    const destDsName = getParamValue(parameters, paramDestDs);
    const destPath = getParamValue(parameters, paramDestPath, '/');
    const format = getParamValue(parameters, paramFormat);
    const resultFileName = getParamValue(parameters, transformResultFilename);
    const toString = getBoolParamValue(parameters, transformToString);
    const excludeRoot = getBoolParamValue(parameters, transformExcludeRoot);
    const placement = getParamValue(
        parameters,
        transformPlacement,
        PLACEMENT_REPLACE,
    );
    const useDocument = getBoolParamValue(parameters, transformUseDocument);
    const setResult = getBoolParamValue(parameters, transformSetResult);
    const xPathEngine = template.getXPathEngine();

    const sourceDocument = await getTransformSourceDs(
        dataSources,
        template,
        sourceDsName,
        sourceFile,
    );

    const sourceNode = selectNodeFromXPathEngine(
        xPathEngine,
        sourcePath,
        sourceDocument,
        errorFailedToSelectSource,
    );
    const destDataSource = getDataSource(dataSources, destDsName);
    const destNode = destDataSource.selectSingle(destPath);

    if (!destNode) {
        throw new Error(errorFailedToSelectDestination);
    }

    const operationParameters = {
        sourceNode,
        destNode,
        produceStringResult: toString,
        excludeRoot,
        format,
        resultFileName,
        xsltFileName,
        placement: getPlacement(placement),
        useDocument,
    };

    const result = await performTransform(transforms, operationParameters);
    const resultToReturn = setResult
        ? transformResultToString(result, excludeRoot)
        : '';

    try {
        await trySetResult(xPathEngine, operationParameters, result);

        return resultToReturn;
    } catch (e) {
        console.error(e);

        return failure(e.message, resultToReturn);
    }
}

const Transform = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default Transform;
